import { z } from 'zod';
import { fetch } from '@/modules/fetch';
import { showNotification } from '@/modules/show-notification';
import {
  FetchApiError,
  gatewayFetch,
  parseFailOpen,
} from '@/modules/fetch-core';
import { readJsonBody } from '@/modules/read-json-body';
import { endpointSchemas } from './schemas';
import type { EndpointId, EndpointResponses } from './schemas';

let onSessionExpired: (() => void) | null = null;

// Single-slot registration (a later call replaces the handler, null clears
// the slot back to the pre-registration boot state) of the session-death
// reaction — teardown lives with the consumer. Only the JWT_EXPIRED- and
// SESSION_REVOKED-message 401s fire it; a plain 401 doesn't.
export function setOnSessionExpired(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

let sessionRefresher: (() => Promise<string | null>) | null = null;
let inflightRefresh: Promise<string | null> | null = null;

// Single-slot registration of the refresher: mints a fresh access token
// from the persisted refresh token, resolving null when no session exists
// or the refresh is rejected. While one is registered, a JWT_EXPIRED 401
// first runs one refresh-and-retry round instead of tearing the session
// down; concurrent expired requests share a single in-flight refresh (one
// mint, every waiter retries with its result).
export function setSessionRefresher(
  handler: (() => Promise<string | null>) | null,
): void {
  sessionRefresher = handler;
}

// Stryker disable next-line ArrowFunction: equivalent — undefined and null both fail the retry's != null guard identically
const refreshRejectionToNull = (): null => null;

function parseBySchema(endpointId: string, raw: unknown): unknown {
  // Widening assignment (not a cast): indexing the registry by an arbitrary
  // endpoint-id string needs the per-key type map erased to z.ZodType.
  const registry: Record<string, z.ZodType> = endpointSchemas;
  const schema = registry[endpointId];
  // Stryker disable next-line ConditionalExpression: defensive fail-open — unreachable today (every switch endpoint id has a registry entry, and an id outside the switch throws before parsing), kept so an id added to the switch without a schema degrades to raw passthrough instead of a crash
  if (!schema) return raw;
  return parseFailOpen('fetchApi', endpointId, schema, raw);
}

const jwtExpiredBodySchema = z.looseObject({
  message: z.literal('JWT_EXPIRED'),
});

// A 401 whose body message is SESSION_REVOKED means the server-side device
// session was ended elsewhere (logout on another device, an SSO
// back-channel logout, or a newer token pair superseding this one). Every
// endpoint checks the session row, so this can arrive on any request.
// Distinct from JWT_EXPIRED: a refresh cannot help — the refresh token
// died with the same session — so no refresh round runs; the session
// teardown fires directly.
const sessionRevokedBodySchema = z.looseObject({
  message: z.literal('SESSION_REVOKED'),
});

// Both read the body sendWithSession extracted from a 401 alone (undefined
// for every other status), so the status gate lives there, not here.
const isJwtExpired = (body401: unknown): boolean =>
  jwtExpiredBodySchema.safeParse(body401).success;
const isSessionRevoked = (body401: unknown): boolean =>
  sessionRevokedBodySchema.safeParse(body401).success;

const GET = 'GET';
const POST = 'POST';
const PUT = 'PUT';
const DELETE = 'DELETE';

type ArgsNoUrlParams = [
  (
    | 'GET_ISSUER'
    | 'PUT_ISSUER'
    | 'GET_AUTH'
    | 'POST_AUTH_INVITE'
    | 'POST_AUTH_REFRESH'
    | 'POST_AUTH_LOGOUT'
    | 'GET_SSO_CONFIG'
    | 'POST_SSO_EXCHANGE'
    | 'GET_AUTH_ACCOUNTS'
    | 'POST_AUTH_SWITCH'
    | 'POST_AUTH_SWITCH_CALLBACK'
    | 'POST_ISSUER_TUTORIALS'
    | 'POST_DELETE_NOTIFICATIONS'
    | 'GET_TOTAL_ISSUERS'
    | 'POST_ISSUER_GDPR'
    | 'GET_NOTIFICATIONS'
    | 'GET_NOTIFICATIONS_STATUS'
    | 'PUT_NOTIFICATIONS_READ'
    | 'GET_SMARTS'
    | 'GET_TELEGRAM_AUTH'
    | 'GET_INVITE_CODES'
    | 'POST_INVITE_CODES_GENERATE'
  ),
  params?: {
    signal: AbortSignal | undefined;
    queryParams?: Record<string, string>;
    body?: Record<string, string | number | boolean | null>;
  },
  accessToken?: string | undefined,
];
// Own body family: the disable-all endpoint takes an ARRAY of per-issuer
// disable rows.
type ArgsDisableAllNotifications = [
  'POST_DELETE_ALL_NOTIFICATIONS',
  params?: {
    signal: AbortSignal | undefined;
    queryParams?: Record<string, string>;
    body?: { source: string; issuerId: string; disable: boolean }[];
  },
  accessToken?: string | undefined,
];
type ArgsOneUrlParam = [
  (
    | 'GET_USER_INFO'
    | 'POST_NOTE_USER'
    | 'GET_USER_FORECASTS'
    | 'POST_FOLLOWINGS'
    | 'DELETE_FOLLOWINGS'
    | 'PUT_NOTIFICATION'
    | 'GET_ABI'
  ),
  {
    signal: AbortSignal | undefined;
    queryParams?: Record<string, string>;
    urlParams: [string];
    body?: Record<string, string | number | boolean | null>;
  },
  accessToken?: string | undefined,
];

// Own body family: `note: null` is a meaningful value (clears the note), so
// the shared string-valued body shape doesn't fit.
type ArgsInviteCodeNote = [
  'PUT_INVITE_CODE_NOTE',
  {
    signal: AbortSignal | undefined;
    queryParams?: Record<string, string>;
    urlParams: [string];
    body: { note: string | null };
  },
  accessToken?: string | undefined,
];

// Own body family: the resolve endpoint takes an ARRAY of addresses (at
// most 500 per request — the server 400s a longer one).
type ArgsResolveIssuers = [
  'POST_ISSUERS_RESOLVE',
  {
    signal: AbortSignal | undefined;
    queryParams?: Record<string, string>;
    body: { addresses: string[] };
  },
  accessToken?: string | undefined,
];

// queryParams values append to the URL as given; body JSON-stringifies —
// values are the JSON primitives (string | number | boolean | null:
// PUT_ISSUER clears fields with null, the notification-disable body carries
// a boolean, POST_AUTH_REFRESH's expiresIn is a number of SECONDS — the
// swagger types it as a unit-less integer; verified live 2026-06-18: the
// minted JWT's exp − iat equals the passed value, and the token 401s past
// it).
type FetchApiRawArgs =
  | ArgsNoUrlParams
  | ArgsDisableAllNotifications
  | ArgsOneUrlParam
  | ArgsInviteCodeNote
  | ArgsResolveIssuers;

// Splits each args family's id union into per-id tuples so fetchApiRaw's
// typed overload can correlate an id K with its exact params family.
type DistributePerId<T> =
  T extends readonly [infer Ids extends string, ...infer Rest] ?
    Ids extends unknown ?
      [Ids, ...Rest]
    : never
  : never;

export type RestArgsFor<K extends EndpointId> =
  DistributePerId<FetchApiRawArgs> extends infer D ?
    D extends readonly [K, ...infer Rest] ?
      Rest
    : never
  : never;

// The id's params slot alone (token excluded) — undefined-including exactly
// when the id's family makes params optional. The delegation face below and
// fetchApi's own signature are typed from this, so a caller's id picks its
// exact options shape.
export type ParamsSlotFor<K extends EndpointId> = Extract<
  DistributePerId<FetchApiRawArgs>,
  readonly [K, ...unknown[]]
>[1];

// Fires one request immediately — no auth-ready gating, bearer token only
// when passed explicitly (fit for auth-time flows holding tokens the atom
// doesn't yet). Targets the origin in VITE_BACKEND_URL_API (prod vs stage
// is baked in at build time); each endpoint id maps to a fixed method+path
// pair, with urlParams[0] substituted into the path where the id's arg
// shape demands it. An ok response whose FULLY RECEIVED body is empty or
// non-JSON resolves as undefined (204s, some DELETEs; non-JSON drift on a
// schema'd endpoint surfaces through the schema report), while a body that
// fails to arrive — the stream dying after the status line (machine sleep
// mid-request, dropped connection) — rejects like any network error; ok
// bodies parse through the endpoint's registered schema (fail-open) here
// rather than in fetchApi, so the auth-time callers get it too. Non-ok
// throws FetchApiError; a 401 whose body message is JWT_EXPIRED first runs
// one refresh-and-retry round through the registered session refresher
// (when one is registered and the caller hasn't aborted) and only an
// unrefreshable/still-expired outcome shows the expiry notification and
// fires the registered session-expired handler before throwing; a 401
// whose body message is SESSION_REVOKED skips the refresh round and shows
// its notification and fires the same handler directly — except on the
// refresh endpoint itself (the caller's expiry path already tears down
// once) and on the logout endpoint (its 401 means "already logged out").
// A response landing after the caller's signal already aborted rejects
// with the signal's abort reason instead of data or FetchApiError —
// callers' cancellation semantics hold even when the race resolves
// response-first. Every response's status feeds
// @/modules/backend-down-signal as backend 'sanr' before any other
// handling (aborted responses included), so gateway-outage detection never
// depends on how the caller consumes the rejection.
export function fetchApiRaw<K extends EndpointId>(
  endpointId: K,
  ...rest: RestArgsFor<K>
): Promise<EndpointResponses[K]>;
// The delegation face: options and token as separate slots, per-id typed
// even when K stays generic at the call site (fetchApi's delegation).
export function fetchApiRaw<K extends EndpointId>(
  endpointId: K,
  options: ParamsSlotFor<K> | undefined,
  accessToken: string | undefined,
): Promise<EndpointResponses[K]>;
export function fetchApiRaw(
  ...looseArgs: [EndpointId, unknown?, (string | undefined)?]
): Promise<unknown> {
  // Re-correlate the widened implementation tuple: both public faces admit
  // only id-matched params, so the family union holds by construction (the
  // width exists only because TS can't check a generic overload against a
  // correlated-union implementation signature).
  const args = looseArgs as FetchApiRawArgs;
  const [endpointId, params, accessToken] = args;
  const [method, base] = (() => {
    switch (endpointId) {
      case 'GET_USER_INFO': {
        const [username] = params.urlParams;
        return [GET, `leaderboards/players/${username}`];
      }
      case 'POST_NOTE_USER': {
        const [username] = params.urlParams;
        return [POST, `leaderboards/players/${username}/writeNote`];
      }
      case 'GET_ISSUER':
        return [GET, 'issuer'];
      case 'PUT_ISSUER':
        return [PUT, 'issuer'];
      case 'POST_ISSUER_TUTORIALS':
        return [POST, 'issuer/tutorials'];
      case 'GET_USER_FORECASTS': {
        const [username] = params.urlParams;
        return [GET, `leaderboards/players/${username}/forecasts`];
      }
      case 'GET_AUTH':
        return [GET, 'auth'];
      case 'POST_AUTH_INVITE':
        return [POST, 'auth/invite'];
      case 'POST_AUTH_REFRESH':
        return [POST, 'auth/refresh'];
      case 'POST_AUTH_LOGOUT':
        return [POST, 'auth/logout'];
      case 'GET_SSO_CONFIG':
        return [GET, 'auth/sso/config'];
      case 'POST_SSO_EXCHANGE':
        return [POST, 'auth/sso/exchange'];
      case 'GET_AUTH_ACCOUNTS':
        return [GET, 'auth/accounts'];
      case 'POST_AUTH_SWITCH':
        return [POST, 'auth/switch'];
      case 'POST_AUTH_SWITCH_CALLBACK':
        return [POST, 'auth/switch/callback'];
      case 'POST_FOLLOWINGS': {
        const [issuerId] = params.urlParams;
        return [POST, `followings/${issuerId}`];
      }
      case 'POST_DELETE_NOTIFICATIONS': {
        return [POST, 'issuer/disableNotifications'];
      }
      case 'POST_DELETE_ALL_NOTIFICATIONS': {
        return [POST, 'issuer/disableNotificationsForGroup'];
      }
      case 'DELETE_FOLLOWINGS': {
        const [issuerId] = params.urlParams;
        return [DELETE, `followings/${issuerId}`];
      }
      case 'GET_TOTAL_ISSUERS': {
        return [GET, 'leaderboards/players'];
      }
      case 'POST_ISSUER_GDPR':
        return [POST, 'issuer/gdpr'];
      case 'POST_ISSUERS_RESOLVE':
        return [POST, 'issuers/resolve'];
      case 'GET_NOTIFICATIONS':
        return [GET, 'notifications'];
      case 'GET_NOTIFICATIONS_STATUS':
        return [GET, 'issuer/getNotificationsStatus'];
      case 'PUT_NOTIFICATION': {
        const [notificationId] = params.urlParams;
        return [PUT, `notifications/${notificationId}/read`];
      }
      case 'PUT_NOTIFICATIONS_READ': {
        return [PUT, 'notifications/read'];
      }
      case 'GET_SMARTS': {
        return [GET, 'smarts'];
      }
      case 'GET_ABI': {
        const [address] = params.urlParams;
        return [GET, `abi/${address}`];
      }
      case 'GET_TELEGRAM_AUTH': {
        return [GET, 'telegramAuth'];
      }
      case 'GET_INVITE_CODES': {
        return [GET, 'invite-codes'];
      }
      case 'POST_INVITE_CODES_GENERATE': {
        return [POST, 'invite-codes/generate'];
      }
      case 'PUT_INVITE_CODE_NOTE': {
        const [inviteCodeId] = params.urlParams;
        return [PUT, `invite-codes/${inviteCodeId}/note`];
      }
    }
  })();
  let url = `${import.meta.env.VITE_BACKEND_URL_API}/${base}`;
  if (params?.queryParams) {
    url += `?${Object.entries(params.queryParams)
      .map(([key, value]) => `${key}=${value}`)
      .join('&')}`;
  }
  const buildOptions = (token: string | undefined): RequestInit => {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    if (token != null && token !== '') {
      headers.append('Authorization', `Bearer ${token}`);
    }
    const options: RequestInit = {
      method,
      headers,
    };
    if (params?.signal) {
      options.signal = params.signal;
    }
    if (params?.body) {
      options.body = JSON.stringify(params.body);
    }
    return options;
  };
  return (async () => {
    const response = await sendWithSession(
      (options) => gatewayFetch('sanr', url, options),
      buildOptions,
      accessToken,
      { endpointId, signal: params?.signal },
    );
    const responseBody = await readJsonBody(response);
    if (response.ok) {
      return parseBySchema(endpointId, responseBody);
    }
    throw new FetchApiError(
      `${method} ${endpointId} → ${response.status}`,
      responseBody,
      {
        endpointId,
        method,
        url,
        status: response.status,
        responseBody,
        requestBody: params?.body,
        queryParams: params?.queryParams,
      },
    );
  })();
}

// The session pipeline both request faces share: one send, then the 401
// handling. A JWT_EXPIRED body runs one refresh-and-retry round through
// the registered refresher — single-flight across concurrent callers,
// skipped on the refresh endpoint itself (its own expired-refresh-token
// 401 must terminate as an expiry, never recurse into another refresh —
// the single-flight promise would await itself) and for an already-aborted
// caller. A still-expired outcome, or a SESSION_REVOKED body, notifies and
// fires the session-expired handler — revocation skipped on the refresh
// endpoint (its revocation surfaces through the caller's expiry path: one
// message, one teardown) and on logout (non-idempotent by contract, so its
// 401 means "already logged out" and the deliberate sign-out must not be
// interrupted). A response landing after the caller aborted rejects with
// the abort reason. Only a 401's body is read here, from a clone — the
// Response returns with its own body untouched, so a streamed 200 stays a
// live stream.
async function sendWithSession(
  send: (options: RequestInit) => Promise<Response>,
  buildOptions: (token: string | undefined) => RequestInit,
  accessToken: string | undefined,
  policy: { endpointId: EndpointId | null; signal: AbortSignal | undefined },
): Promise<Response> {
  const bodyOf401 = async (response: Response): Promise<unknown> =>
    response.status === 401 ? readJsonBody(response.clone()) : undefined;
  let response = await send(buildOptions(accessToken));
  let body = await bodyOf401(response);
  if (
    isJwtExpired(body) &&
    sessionRefresher !== null &&
    policy.endpointId !== 'POST_AUTH_REFRESH' &&
    policy.signal?.aborted !== true
  ) {
    inflightRefresh ??= sessionRefresher()
      .catch(refreshRejectionToNull)
      .finally(() => {
        inflightRefresh = null;
      });
    const freshToken = await inflightRefresh;
    if (freshToken != null) {
      response = await send(buildOptions(freshToken));
      body = await bodyOf401(response);
    }
  }
  if (isJwtExpired(body)) {
    showNotification('error', 'Session is expired');
    onSessionExpired?.();
  }
  if (
    isSessionRevoked(body) &&
    policy.endpointId !== 'POST_AUTH_REFRESH' &&
    policy.endpointId !== 'POST_AUTH_LOGOUT'
  ) {
    showNotification('error', 'You were signed out');
    onSessionExpired?.();
  }
  if (policy.signal?.aborted === true) {
    throw policy.signal.reason;
  }
  return response;
}

// The request shape for routes outside the endpoint registry: `headers`
// are the caller's own (Accept, Content-Type) — the bearer is added by the
// pipeline.
export type RawRequestInit = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: string;
  signal?: AbortSignal;
};

// The raw-response face for SanR-backend routes the endpoint registry
// doesn't model — the /openarena proxy to the personal agent: streamed
// bodies, GraphQL, paths outside the swagger. `path` is relative to
// VITE_BACKEND_URL_API. Same bearer and same session pipeline as
// fetchApiRaw (sendWithSession), but the Response comes back unread — the
// caller owns status handling and the body. Statuses do NOT feed
// @/modules/backend-down-signal: the proxy forwards the upstream's own
// 502/503 verbatim, which say nothing about the SanR gateway.
export function fetchApiRawResponse(
  path: string,
  init: RawRequestInit,
  accessToken: string | undefined,
): Promise<Response> {
  const url = `${import.meta.env.VITE_BACKEND_URL_API}/${path}`;
  const buildOptions = (token: string | undefined): RequestInit => {
    const headers = new Headers(init.headers);
    if (token != null && token !== '') {
      headers.append('Authorization', `Bearer ${token}`);
    }
    const options: RequestInit = { method: init.method ?? 'GET', headers };
    if (init.signal) options.signal = init.signal;
    if (init.body !== undefined) options.body = init.body;
    return options;
  };
  return sendWithSession(
    (options) => fetch(url, options),
    buildOptions,
    accessToken,
    { endpointId: null, signal: init.signal },
  );
}
